import type {
  EnvelopeStatus,
  PartyType,
  RecipientRole,
  RecipientStatus,
  SubjectType
} from "./esign.types.ts"

// Mirrors apps.esign.tracking in ezbuild_back.
export const TRACKING_STAGES = ["out_for_signature", "awaiting_countersignature"] as const
export type TrackingStage = (typeof TRACKING_STAGES)[number]

export const TILE_IDS = [
  "out_for_signature",
  "awaiting_countersignature",
  "fully_signed_month",
  "declined_or_expired_month"
] as const
export type TileId = (typeof TILE_IDS)[number]

export const TRACKING_ORDERINGS = ["sent", "-sent", "last_activity", "-last_activity"] as const
export type TrackingOrdering = (typeof TRACKING_ORDERINGS)[number]

/** Every time is the API's: "YYYY-MM-DD HH:mm:ss" in UTC — read it with parseApiTime. */
export interface RecipientActivity {
  opens: number
  downloads: number
  first_sent: string | null
  first_link_opened: string | null
  first_opened: string | null
  last_opened: string | null
  last_bounced: string | null
}

export interface TrackedRecipient {
  id: string
  party_type: PartyType
  role: RecipientRole
  name: string
  email: string
  signing_order: number
  status: RecipientStatus
  signed: string | null
  activity: RecipientActivity
}

export interface TrackedEnvelope {
  id: string
  status: EnvelopeStatus
  stage: TrackingStage | null
  subject: { type: SubjectType; id: string; name: string; version: number }
  record: {
    id: string
    uid: string | null
    name: string | null
    is_project: boolean
    customer_name: string | null
  }
  sent: string | null
  sent_by: { id: string; name: string } | null
  expires_at?: string | null
  waiting_since: string | null
  signed_at: string | null
  ended_at: string | null
  last_activity: string | null
  overdue: boolean
  nearing_expiry: boolean
  reissues: number
  recipients: TrackedRecipient[]
}

export interface TrackingPage {
  count: number
  next: string | null
  previous: string | null
  results: TrackedEnvelope[]
}

export interface TrackingTile {
  id: TileId
  title: string
  value: number
  unit: "count"
  basis: "snapshot" | "period"
  drill_to: string
  compare?: { delta: number; delta_pct: number | null; label: string; value: number }
}

export interface Measure {
  median_seconds: number | null
  measured: number
  running: number
}

export interface TrackingSummary {
  time_to_first_open: Measure
  cycle_time: Measure
  our_delay: Measure
  open_rate: { rate: number | null; opened: number; sent: number }
  decline_rate: { rate: number | null; declined: number; finished: number }
}

export interface TrackingFilterOptions {
  senders: Array<{ id: string; name: string }>
}

export interface TrackingFilters {
  status: EnvelopeStatus[]
  document_type: SubjectType[]
  party_type: PartyType[]
  sent_by: string[]
  sent_after: string | null
  sent_before: string | null
  tile: TileId | null
  q: string
  ordering: TrackingOrdering | null
}

export const EMPTY_FILTERS: TrackingFilters = {
  status: [],
  document_type: [],
  party_type: [],
  sent_by: [],
  sent_after: null,
  sent_before: null,
  tile: null,
  q: "",
  ordering: null
}

/** What the API is sent: comma-joined lists, nothing the user has not set. */
export type TrackingQuery = Partial<Record<keyof TrackingFilters, string>> & {
  page?: number | string
  page_size?: number | string
}

export interface EnvelopeEventRow {
  id: string
  event_type: string
  event_type_label: string
  recipient: { id: string; name: string; role: RecipientRole } | null
  actor: { id: string | null; name: string; email: string } | null
  metadata: Record<string, unknown>
  evidence: { ip_address: string | null; user_agent: string; forwarded_for: string[] } | null
  created: string
}

export interface EventsPage {
  count: number
  next: string | null
  results: EnvelopeEventRow[]
}
